import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { formatGermanNumber } from "../../shared/utils/functions";
import { Card, CardBody, Col, Form, Row, Nav, Tab } from "react-bootstrap";
import Select from "react-select";
import { IMaterial } from "../../shared/interfaces/material/IMaterial";
import { MaterialService } from "./service";
import { CustomPagination } from "../../shared/components/CustomPagination";
import { usePagination } from "../../shared/hooks/usePagination";
import DeleteModal from "../../Common/DeleteModal";
import { MaterialTypeService } from "../material-types/service";
import { IMaterialType } from "../../shared/interfaces/material-type/IMaterialType";

const PER_PAGE_DEFAULT = 20;

const MaterialList = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [list, setList] = useState<IMaterial[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [materialToDelete, setMaterialToDelete] = useState<number | null>(null);
    const [isUsedInJob, setIsUsedInJob] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [materialTypes, setMaterialTypes] = useState<IMaterialType[]>([]);
    const [activeTab, setActiveTab] = useState<string>(searchParams.get("materialType") || "all");
    const {
        page,
        handlePages,
        pagination,
        totalPages,
        setTotalPages,
        changeFilterHandler,
        goToPreviousPage,
        goToNextPage,
    } = usePagination({
        perPage: PER_PAGE_DEFAULT,
    });
    const navigate = useNavigate();

    const entriesPerPageOptions = [
        { label: "10", value: 10 },
        { label: "25", value: 25 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
        { label: "200", value: 200 },
    ];

    // Fetch material types for tabs
    useEffect(() => {
        MaterialTypeService.getAll({ page: 1, perPage: 200 })
            .then((response) => {
                setMaterialTypes(response.data || []);
            })
            .catch(ErrorToast);
    }, []);

    useEffect(() => {
        const params = activeTab === "all" ? pagination : { ...pagination, materialType: Number(activeTab) };
        MaterialService.getAll(params)
            .then((response) => {
                const data = response.data;
                setList(data);
                setTotalPages(response.totalPages);
            })
            .catch(ErrorToast);
    }, [pagination, setTotalPages, activeTab]);

    const handleCopy = (id: number) => {
        MaterialService.copy(id)
            .then(() => {
                SuccessToast(t("material.copySuccess"));
                const params = activeTab === "all" ? pagination : { ...pagination, materialType: Number(activeTab) };
                MaterialService.getAll(params)
                    .then((response) => {
                        setList(response.data);
                        setTotalPages(response.totalPages);
                    })
                    .catch(ErrorToast);
            })
            .catch(ErrorToast);
    };

    const handleDeleteClick = (id: number, usedInJob: boolean) => {
        setMaterialToDelete(id);
        setIsUsedInJob(usedInJob);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = () => {
        if (materialToDelete !== null) {
            MaterialService.delete(materialToDelete)
                .then(() => {
                    SuccessToast(t("material.deleteSuccess"));
                    setShowDeleteModal(false);
                    setMaterialToDelete(null);
                    setIsUsedInJob(false);
                    const params = activeTab === "all" ? pagination : { ...pagination, materialType: Number(activeTab) };
                    MaterialService.getAll(params)
                        .then((response) => {
                            const data = response.data;
                            setList(data);
                            setTotalPages(response.totalPages);
                        })
                        .catch(ErrorToast);
                })
                .catch(ErrorToast);
        }
    };

    const handleTabChange = (tabKey: string | null) => {
        if (tabKey) {
            setActiveTab(tabKey);
            if (tabKey === "all") {
                searchParams.delete("materialType");
            } else {
                searchParams.set("materialType", tabKey);
            }
            setSearchParams(searchParams);
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const allowedTypes = [
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "text/csv",
        ];

        if (!allowedTypes.includes(file.type)) {
            ErrorToast(t("material.invalidFileType"));
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        setUploading(true);
        MaterialService.upload(formData)
            .then(() => {
                SuccessToast(t("material.importSuccess"));
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                // Refresh the list
                const params = activeTab === "all" ? pagination : { ...pagination, materialType: Number(activeTab) };
                MaterialService.getAll(params)
                    .then((response) => {
                        const data = response.data;
                        setList(data);
                        setTotalPages(response.totalPages);
                    })
                    .catch(ErrorToast);
            })
            .catch(ErrorToast)
            .finally(() => setUploading(false));
    };

    return (
        <React.Fragment>
            <Col>
                <Card>
                    <CardBody>
                        <div className="text-end mb-3">
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                accept=".xlsx,.xls,.csv"
                                onChange={handleFileUpload}
                            />
                            <button
                                className="btn btn-success text-end me-2"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                            >
                                <i className="ti ti-upload f-18"></i>{" "}
                                {uploading ? t("general.uploading") : t("material.import")}
                            </button>
                            <button
                                className="btn btn-primary text-end"
                                onClick={() => navigate("/materials/create")}
                            >
                                <i className="ti ti-plus f-18"></i> {t("material.add")}
                            </button>
                        </div>

                        <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
                            <Nav variant="tabs" className="mb-3">
                                <Nav.Item>
                                    <Nav.Link eventKey="all">{t("general.all")}</Nav.Link>
                                </Nav.Item>
                                {materialTypes.map((type) => (
                                    <Nav.Item key={type.id}>
                                        <Nav.Link eventKey={String(type.id)}>{type.name}</Nav.Link>
                                    </Nav.Item>
                                ))}
                            </Nav>
                            <Tab.Content>
                                <Tab.Pane eventKey={activeTab}>

                                    <div className="d-sm-flex align-items-center mb-3">
                                        <ul className="list-inline me-auto my-1">
                                            <li className="list-inline-item">
                                                <Select
                                                    placeholder={t("job.entriesPerPage")}
                                                    options={entriesPerPageOptions}
                                                    onChange={(selected) =>
                                                        changeFilterHandler(
                                                            "perPage",
                                                            selected?.value ?? PER_PAGE_DEFAULT
                                                        )
                                                    }
                                                    defaultValue={entriesPerPageOptions.find(
                                                        (option) => option.value === PER_PAGE_DEFAULT
                                                    )}
                                                    classNamePrefix="react-select"
                                                />
                                            </li>
                                            <span> {t("job.entriesPerPage")}</span>
                                        </ul>

                                        <ul className="list-inline ms-auto my-1 mt-3">
                                            <li className="list-inline-item ms-auto">
                                                <Form.Control
                                                    type="text"
                                                    placeholder={t("general.search")}
                                                    onChange={(e) =>
                                                        changeFilterHandler("term", e.target.value)
                                                    }
                                                />
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table table-hover" id="pc-dt-simple-1">
                                            <thead>
                                                <tr>
                                                    <th>{t("material.id")}</th>
                                                    <th>{t("material.name")}</th>
                                                    <th>{t("material.materialType")}</th>
                                                    <th>{t("material.articleNumber")}</th>

                                                    <th>{t("material.costPrice")}</th>
                                                    <th>{t("material.purchasePrice")}</th>
                                                    <th>{t("material.recommendedRetailPrice")}</th>
                                                    <th>{t("material.supplier")}</th>
                                                    <th>{t("material.updatedAt")}</th>
                                                    <th className="text-end" style={{ position: 'sticky', right: 0, background: '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' }}>{t("material.actions")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {list.map((item: IMaterial, index: number) => (
                                                    <tr key={index}>
                                                        <td>{item.id}</td>
                                                        <td>
                                                            {item.name}
                                                            {item.internalName && (
                                                                <div className="text-muted f-12">{item.internalName}</div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {(item as any).materialType?.name ??
                                                                (item as any).materialType ??
                                                                "-"}
                                                        </td>
                                                        <td>{item.articleNumber}</td>
                                                        <td>{formatGermanNumber((item as any).costPrice)}</td>
                                                        <td>{formatGermanNumber(item.purchasePrice)}</td>
                                                        <td>{formatGermanNumber(item.recommendedRetailPrice)}</td>

                                                        <td>{(item as any).supplier?.name ?? "-"}</td>
                                                        <td>
                                                            {item.updatedAt
                                                                ? new Date(item.updatedAt).toLocaleDateString()
                                                                : "-"}
                                                        </td>
                                                        <td className="text-end" style={{ position: 'sticky', right: 0, background: '#fff', boxShadow: '-2px 0 5px rgba(0,0,0,0.05)' }}>
                                                            <ul className="list-inline mb-0">
                                                                <li
                                                                    className="list-inline-item"
                                                                    onClick={() =>
                                                                        navigate(`/materials/edit/${item.id}`)
                                                                    }
                                                                >
                                                                    <a
                                                                        href="#"
                                                                        className="avtar avtar-s btn-link-success btn-pc-default"
                                                                    >
                                                                        <i className="ti ti-edit f-20"></i>
                                                                    </a>
                                                                </li>
                                                                <li className="list-inline-item">
                                                                    <span
                                                                        role="button"
                                                                        className="avtar avtar-s btn-link-secondary btn-pc-default"
                                                                        onClick={() => item.id && handleCopy(item.id)}
                                                                    >
                                                                        <i className="ti ti-copy f-20"></i>
                                                                    </span>
                                                                </li>
                                                                <li className="list-inline-item">
                                                                    <span
                                                                        role="button"
                                                                        className="avtar avtar-s btn-link-danger btn-pc-default"
                                                                        onClick={() =>
                                                                            item.id && handleDeleteClick(item.id, (item as any).isUsedInJob ?? false)
                                                                        }
                                                                    >
                                                                        <i className="ti ti-trash f-20"></i>
                                                                    </span>
                                                                </li>
                                                            </ul>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <CustomPagination
                                        page={page}
                                        totalPages={totalPages}
                                        handlePages={handlePages}
                                        goToPreviousPage={goToPreviousPage}
                                        goToNextPage={goToNextPage}
                                    />
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>
                    </CardBody>
                </Card>
            </Col>
            <DeleteModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDeleteId={handleDeleteConfirm}
                name={t("material.material")}
                isUsedInJob={isUsedInJob}
            />
        </React.Fragment>
    );
};

export default MaterialList;
